import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ErrorType,
  GetVenueResponse,
  JobStatus,
  apiContext,
  authenticateToConnect,
  v1VenueCreateVenue,
  v1VenueGetJobStatus,
  v1VenueGetVenue,
  v1VenueShouldVerifyEmail,
  v1VerificationSendVerificationCode,
  v1VerificationVerifyCode,
} from '../api-client';
import { getGTMLegacySignupEvent, getGTMSignupEvent } from '../features/gtm';
import {
  SegmentEventType,
  useSegmentContext,
} from '../features/segment-context';
import { useCaptchaContext } from '../features/captcha-context';
import { CODE_LENGTH } from '../steps-contents';
import {
  Deregister,
  FormContextData,
  RegisterValidator,
  Step,
  Validator,
} from './types';
import { getStepConfiguration } from './step-configuration';
import { goToTwBusiness, redirectToConnect } from './redirect-utils';

type ErrorState = {
  error: unknown;
  source: 'AboutSignUp' | 'AddInfo' | 'AddServices' | 'AddBusinessType';
};
type FormErrorState = { type: ErrorType; key: string }[];
type StepMachineState = null | GetVenueResponse | ErrorState | FormErrorState;

export function isRedirectState(
  state: StepMachineState,
): state is GetVenueResponse {
  return !!state && 'redirectURL' in state;
}

export function isErrorState(state: StepMachineState): state is ErrorState {
  return !!state && 'error' in state;
}

export function isFormErrorState(
  state: StepMachineState,
): state is FormErrorState {
  return !!state && Array.isArray(state);
}

function stepToSegmentStep(
  step: Step,
): 'AboutSignUp' | 'AddInfo' | 'AddServices' | 'AddBusinessType' | 'unknown' {
  switch (step) {
    case Step.STEP_ONE_SEARCH:
    case Step.STEP_ONE_FORM:
      return 'AddInfo';
    case Step.STEP_TWO_FORM:
      return 'AddServices';
    case Step.STEP_THREE_FORM:
      return 'AddBusinessType';
    case Step.STEP_FOUR_FORM:
      return 'AboutSignUp';
    case Step.STEP_FINAL:
    case Step.STEP_FINAL_REDIRECT_LOGIN:
    case Step.STEP_ERROR:
    default:
      return 'unknown';
  }
}

function getActionFromAddInfoMode(
  addInfoMode?: 'manual' | 'automatic',
): 'search' | 'manual' | 'skip' {
  if (addInfoMode === 'manual') {
    return 'manual';
  }
  if (addInfoMode === 'automatic') {
    return 'search';
  }
  return 'skip';
}

export type StepMachine = {
  step: Step;
  state: StepMachineState;
  loading: boolean;
  onNext?: (params?: { addInfoMode?: 'manual' | 'automatic' }) => Promise<void>;
  onPrevious?: () => Promise<void>;
  registerValidator: RegisterValidator;
};

const POLLING_RATE = 10_000;
const MAX_ATTEMPTS = 20;
const waitForJob = async (jobId: string) => {
  let status: JobStatus | null = null;
  let attempt = 0;
  do {
    attempt += 1;
    const { status: statusResponse } = await v1VenueGetJobStatus(apiContext, {
      jobId,
    });
    status = statusResponse;
    if (status && status !== JobStatus.PENDING) {
      return status;
    }
    if (attempt > MAX_ATTEMPTS) {
      throw new Error('There was an error while creating your venue');
    }
    await new Promise(resolve => setTimeout(resolve, POLLING_RATE));
  } while (!status || status === JobStatus.PENDING);
};

const getLpPartners = () => {
  const cookie = document?.cookie;
  if (!cookie && typeof cookie === 'string') {
    return;
  }

  const lpPartners = cookie
    .split('; ')
    .find(row => row.startsWith('lp_partners='))
    ?.split('=')[1];

  return decodeURIComponent(lpPartners || '');
};

export function useStepMachine(
  localStorageStep: Step,
  data: FormContextData,
): StepMachine {
  const { executeCaptcha } = useCaptchaContext();
  const [step, setStep] = useState<Step>(localStorageStep);
  const { previousKey } = getStepConfiguration(step);
  const state = useRef<StepMachineState>(null);
  const [loading, setLoading] = useState(false);
  const [validators, setValidators] = useState<Validator[]>([]);
  // addInfoMode is passed in onNext on STEP_ONE_SEARCH but event is trigger at STEP_ONE_FORM
  const addInfoModeRef = useRef<'manual' | 'automatic'>();
  const { trackEvent, identify } = useSegmentContext();
  const { t } = useTranslation('stepError');

  const signUp = useCallback(async () => {
    trackEvent(SegmentEventType.ABOUT_SIGN_UP, {
      step: 'AboutSignUp',
      marketing_opt_in: data.acceptMarketing || false,
    });

    const signupGTMData = await getGTMSignupEvent({ email: data.email });
    window.dataLayer?.push(signupGTMData);
    window.dataLayer?.push(getGTMLegacySignupEvent());

    state.current = null;
    setLoading(true);

    try {
      const token = await executeCaptcha('self_signup/sign_up');

      const { jobId, errors } = await v1VenueCreateVenue(
        apiContext,
        {
          lp_partners: getLpPartners(),
        },
        {
          businessName: data.businessName,
          businessType: data.businessType!, // should be enforced by form validation
          dedicatedSpace: !data.dedicatedSpace,
          businessSegments: data.businessSegments,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          password: data.password,
          phoneNumber: data.phoneNumber,
          address: data.address,
          acceptsMarketing: data.acceptMarketing,
          verificationCode: data.verificationCode,
          leadId: data.leadId,
          opportunityId: data.opportunityId,
          gaSessionId: sessionStorage.getItem('ga_session_id') || '',
          token,
        },
        {
          credentials: 'include', // Send cookies
        },
      );
      if (errors.length) {
        state.current = errors;
        trackEvent(SegmentEventType.ERROR_MESSAGE_SHOWN, {
          step: 'AboutSignUp',
          errors: errors.map(e => `${e.key} ${e.type}`),
        });
        // In case of errors on the first form, go back to step one
        if (
          errors.map(e => e.key).includes('businessName') ||
          errors.map(e => e.key).includes('address')
        ) {
          setStep(Step.STEP_ONE_FORM);
          return;
        }
        // If the verification code is invalid (expired), go to relevant step
        if (errors.map(e => e.key).includes('verificationCode')) {
          setStep(Step.STEP_EMAIL_VERIFICATION);
        } else {
          setStep(Step.STEP_FOUR_FORM);
        }
        return;
      }
      if (!jobId) {
        return;
      }
      identify(data);
      setStep(Step.STEP_FINAL);
      setLoading(false);
      const status = await waitForJob(jobId);
      if (status === JobStatus.SUCCESS) {
        await authenticateToConnect({
          email: data.email,
          password: data.password,
        });
        redirectToConnect();
      } else {
        state.current = {
          error: t('accountCreation'),
          source: 'AboutSignUp',
        };
        setStep(Step.STEP_ERROR);
      }
    } catch (error) {
      state.current = {
        error,
        source: 'AboutSignUp',
      };
      setStep(Step.STEP_ERROR);
    } finally {
      setLoading(false);
    }
  }, [data, executeCaptcha, identify, trackEvent, t]);

  const shouldVerifyEmail = useCallback(async () => {
    try {
      const token = await executeCaptcha('self_signup/should_verify_email');
      const { shouldVerifyEmail } = await v1VenueShouldVerifyEmail(apiContext, {
        email: data.email,
        token,
      });
      return shouldVerifyEmail;
    } catch (error) {
      state.current = {
        error,
        source: 'AboutSignUp',
      };
      setStep(Step.STEP_ERROR);
    }
  }, [data, executeCaptcha]);

  const findExisting = useCallback(async () => {
    try {
      const token = await executeCaptcha('self_signup/get_venue');
      const response = await v1VenueGetVenue(apiContext, {
        email: data.email,
        token,
      });

      return response.redirectURL;
    } catch (error) {
      state.current = {
        error,
        source: 'AboutSignUp',
      };
      setStep(Step.STEP_ERROR);
    }
  }, [data, executeCaptcha]);

  const sendVerificationCode = useCallback(async () => {
    try {
      const token = await executeCaptcha('self_signup/send_verification_code');
      await v1VerificationSendVerificationCode(apiContext, {
        firstName: data.firstName,
        lastName: data.lastName,
        businessName: data.businessName,
        email: data.email,
        language: navigator.language || data.address.countryCode,
        token,
      });
    } catch (error) {
      state.current = {
        error,
        source: 'AboutSignUp',
      };
      setStep(Step.STEP_ERROR);
    }
  }, [data, executeCaptcha]);

  const verifyAndSignup = useCallback(async () => {
    const needsVerification = await shouldVerifyEmail();
    if (needsVerification) {
      await sendVerificationCode();
      setStep(Step.STEP_EMAIL_VERIFICATION);
    } else {
      await signUp();
    }
  }, [shouldVerifyEmail, signUp, sendVerificationCode]);

  const isValidVerificationCode = useCallback(
    async (code: string) => {
      const token = await executeCaptcha('self_signup/verify_code');
      const response = await v1VerificationVerifyCode(apiContext, {
        code,
        email: data.email,
        token,
      });
      return response.success;
    },
    [data, executeCaptcha],
  );

  const onNext = useCallback(
    async (params?: { addInfoMode?: 'manual' | 'automatic' }) => {
      setLoading(true);
      try {
        const validationResults = await Promise.all(
          validators.map(v => v(data)),
        );
        const invalidResults = validationResults.filter(
          (v): v is { error: string } => !!v.error,
        );
        if (invalidResults.length) {
          trackEvent(SegmentEventType.ERROR_MESSAGE_SHOWN, {
            step: stepToSegmentStep(step),
            errors: invalidResults.map(i => i.error),
          });
          return;
        }

        if (params?.addInfoMode) {
          addInfoModeRef.current = params?.addInfoMode;
        }

        switch (step) {
          case Step.STEP_CONTEXT: {
            trackEvent(SegmentEventType.CONTEXT_STEP_CLICK, {
              step: 'ContextStep',
            });
            setStep(Step.STEP_ONE_SEARCH);
            break;
          }
          case Step.STEP_ONE_SEARCH: {
            trackEvent(SegmentEventType.BUSINESS_SEARCH, {
              step: 'AddInfo',
              action: getActionFromAddInfoMode(params?.addInfoMode),
            });
            setStep(Step.STEP_ONE_FORM);
            break;
          }
          case Step.STEP_ONE_FORM:
            trackEvent(SegmentEventType.ADD_INFO, {
              step: 'AddInfo',
              mode: params?.addInfoMode || addInfoModeRef.current || 'manual',
            });
            setStep(Step.STEP_TWO_FORM);
            break;
          case Step.STEP_TWO_FORM:
            trackEvent(SegmentEventType.ADD_SERVICES, {
              step: 'AddServices',
              services_selection: data.businessSegments,
            });
            setStep(Step.STEP_THREE_FORM);
            break;
          case Step.STEP_THREE_FORM:
            trackEvent(SegmentEventType.ADD_BUSINESS_TYPE, {
              step: 'AddBusinessType',
              business_selection: data.businessType!,
            });
            setStep(Step.STEP_FOUR_FORM);
            break;
          case Step.STEP_FOUR_FORM: {
            const existing = await findExisting();
            if (existing) {
              state.current = {
                redirectURL: existing,
              };
              setStep(Step.STEP_FINAL_REDIRECT_LOGIN);
              return;
            }
            await verifyAndSignup();
            break;
          }
          case Step.STEP_FINAL_REDIRECT_LOGIN: {
            trackEvent(SegmentEventType.CONTINUE_WITH_SIGNUP, {
              step: 'LoginRedirect',
            });
            await verifyAndSignup();
            break;
          }
          case Step.STEP_EMAIL_VERIFICATION: {
            if (!data.verificationCode) {
              return;
            }
            const isValidCode = await isValidVerificationCode(
              data.verificationCode,
            );
            if (!isValidCode) {
              state.current = [
                {
                  key: 'verificationCode',
                  type: ErrorType.INVALID,
                },
              ];
              trackEvent(SegmentEventType.ERROR_MESSAGE_SHOWN, {
                step: 'VerificationCode',
                errors: ['Invalid verification code'],
              });
              return;
            } else {
              await signUp();
            }
            break;
          }
          case Step.STEP_FINAL:
            goToTwBusiness();
            break;
          default:
            throw new Error(`Unhandled step ${step} in onNext`);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      validators,
      step,
      data,
      trackEvent,
      signUp,
      verifyAndSignup,
      findExisting,
      isValidVerificationCode,
    ],
  );

  const onPrevious = useCallback(async () => {
    switch (step) {
      case Step.STEP_TWO_FORM:
        setStep(Step.STEP_ONE_FORM);
        break;
      case Step.STEP_THREE_FORM:
        setStep(Step.STEP_TWO_FORM);
        break;
      case Step.STEP_FOUR_FORM:
        setStep(Step.STEP_THREE_FORM);
        break;
      case Step.STEP_EMAIL_VERIFICATION:
        setStep(Step.STEP_FOUR_FORM);
        break;
      case Step.STEP_FINAL_REDIRECT_LOGIN:
        setStep(Step.STEP_FOUR_FORM);
        break;
      default:
        throw new Error(`Unhandled step ${step} in onPrevious`);
    }
  }, [step]);

  const registerValidator = useCallback((validator: Validator): Deregister => {
    setValidators(currentValidators => [...currentValidators, validator]);
    return () => {
      setValidators(currentValidators =>
        currentValidators.filter(v => v !== validator),
      );
    };
  }, []);

  // Since there is no next button on the verification step, we call it automatically as soon as the code is the right length
  useEffect(() => {
    if (step !== Step.STEP_EMAIL_VERIFICATION) {
      return;
    }
    if (data.verificationCode?.length === CODE_LENGTH) {
      onNext();
    }
  }, [step, data.verificationCode, onNext]);

  return {
    step,
    state: state.current,
    loading,
    onNext,
    onPrevious: previousKey ? onPrevious : undefined,
    registerValidator,
  };
}
